import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps, useModal } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { ImageDetails } from '@/services/api/contracts/catalog/types';

import { FC, PropsWithChildren, Suspense, useState } from 'react';

interface GalleryModalProps {
  onSelect: (image: ImageDetails) => void;
}

import { v4 } from 'uuid';

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/popover/HoverCard';
import SiMaximize from '@/icons/SiMaximize';
import SiPhoto from '@/icons/SiPhoto';
import SiPictureInPicture from '@/icons/SiPictureInPicture';
import SiSearch from '@/icons/SiSearch';
import { cn } from '@/lib/utils';
import { ImageMetadata, useGallery } from '@/services/api/hooks/images';
import { useUser } from '@/services/authentication/hooks/user';
import { Variables } from '@/services/backstage/config';
import { BlobUploadSession } from '@/services/upload/BlobUploadProvider';
import { useClearCompletedSessions, useUploadFiles, useUploadSessions } from '@/services/upload/hooks';
import { UploadStatus } from '@/services/upload/utils';
import { useSearch } from '@/utils/search';
import { useVariable } from '@softwareimaging/backstage';
import { useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { Button } from '../../Button';
import { FileInputContainer } from '../file/shared';
import { TextInput } from '../text/TextInput';
import DeleteGalleryImageModal from './DeleteGalleryImageModal';
import GalleryModalCard from './GalleryModalCard';
import { DEFAULT_THUMBNAIL_WIDTH } from './consts';
import { canvasToBlob, canvasToImageData, createBlurhash, createCanvas, createImage } from './imageHelperHooks';

const processFiles = async (list: FileList | File[], userId: string, imageBaseURL: string) => {
  const meta = new Map<
    string,
    { id: string; metadata: Record<string, string>; extension: string; type: 'original' | 'thumbnail' }
  >();

  const images = Array.from(list);
  const pairs = await Promise.all(
    images.map(async file => {
      const id = v4();

      const image = await createImage(file);
      const ratio = image.height / image.width;
      const canvas = createCanvas(image);
      const pixels = await canvasToImageData(canvas);

      const thumbnailWidth = image.width < DEFAULT_THUMBNAIL_WIDTH ? image.width : DEFAULT_THUMBNAIL_WIDTH;
      const thumbnailHeight = Math.floor(thumbnailWidth * ratio);
      const resizedBitmap = await createImageBitmap(pixels, {
        resizeWidth: thumbnailWidth,
        resizeHeight: thumbnailHeight
      });
      const resizedCanvas = createCanvas(resizedBitmap);
      const hash = await createBlurhash(resizedCanvas, ratio);
      const thumbnail = await canvasToBlob(canvas);

      const originalName = `${id}-original`;
      const thumbnailName = `${id}-thumbnail`;

      const [fileName] = file.name.split('.');
      meta.set(originalName, { id, metadata: {}, extension: 'jpg', type: 'original' });

      const metadata = {
        id,
        hash,
        ratio: ratio.toString(),
        name: fileName,
        uploadedAt: new Date().toISOString(),
        uploadedBy: userId,
        original: `${imageBaseURL}/${originalName}.jpg`,
        type: 'thumbnail'
      };

      meta.set(thumbnailName, { id, metadata, extension: 'jpg', type: 'thumbnail' });

      return [
        new File([file], originalName, { type: file.type }),
        new File([thumbnail], thumbnailName, { type: 'image/jpeg' })
      ];
    })
  );

  const files = pairs.flat();
  return { files, meta };
};

interface PairedUploadSessions {
  original?: BlobUploadSession;
  thumbnail?: BlobUploadSession;
}

const SessionStatus: FC<PropsWithChildren<{ session: BlobUploadSession }>> = ({ session, children }) => {
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <span
          className={cn(
            'p-1 rounded-md bg-gray-200',
            session.status === UploadStatus.Complete && 'bg-green-100 text-green-800',
            session.status === UploadStatus.Failed && 'bg-red-100 text-red-800',
            session.status === UploadStatus.Uploading && 'bg-yellow-100 text-yellow-800',
            session.status === UploadStatus.Interrupted && 'bg-orange-100 text-orange-800'
          )}
        >
          {children}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="flex flex-col gap-1 text-sm">
        <p>Type: {session.meta.type}</p>
        <p>Status: {session.status}</p>
        {session.meta.metadata.name && <p>Original name: {session.meta.metadata.name}</p>}
      </HoverCardContent>
    </HoverCard>
  );
};

const UploadManager = () => {
  const sessions = useUploadSessions<BlobUploadSession>();
  const clearSessions = useClearCompletedSessions();

  // Go through all sessions and pair them up by session.meta.id
  const grouped = sessions
    .filter(session => session.meta.type)
    .reduce(
      (map, session) => {
        const id = session.meta.id;
        if (!map[id]) map[id] = {};

        if (session.meta.type === 'original') map[id].original = session;
        else if (session.meta.type === 'thumbnail') map[id].thumbnail = session;
        else throw new Error('Unknown session type');

        return map;
      },
      {} as Record<string, PairedUploadSessions>
    );

  return (
    <div className="flex space-x-3 justify-between">
      <div className="flex flex-wrap gap-2">
        {Object.entries(grouped).map(([key, { original, thumbnail }]) => {
          return (
            <div className="rounded-lg border flex gap-2 p-2" key={key}>
              <span className={cn('p-1 rounded-md bg-green-100 text-green-800')}>
                <SiMaximize className="text-lg" />
              </span>
              {thumbnail && (
                <SessionStatus session={thumbnail}>
                  <SiPictureInPicture className="text-lg" />
                </SessionStatus>
              )}
              {original && (
                <SessionStatus session={original}>
                  <SiPhoto className="text-lg" />
                </SessionStatus>
              )}
            </div>
          );
        })}
      </div>
      {sessions.length > 0 && <Button onClick={clearSessions}>Clear completed uploads</Button>}
    </div>
  );
};

export const GalleryModal: FC<ModalProps & GalleryModalProps> = ({ onSelect, ...props }) => {
  const Gallery = () => {
    const user = useUser();
    const queryClient = useQueryClient();
    const uploadFiles = useUploadFiles();

    const imageBaseURL = useVariable<Variables>('imageBaseUrl');
    if (!imageBaseURL) throw new Error('Missing image base url');

    const [images] = useGallery();

    const [query, setQuery] = useState('');
    const results = useSearch(images, ['name'], query);

    const [loading, setLoading] = useState(false);
    // const retryUploadSessions = useRetryUploadSessions();

    const onInput = async (list: FileList | null | File[]) => {
      setLoading(true);
      if (!list) return;
      const { files, meta } = await processFiles(list, user.email, imageBaseURL);
      await uploadFiles(files, { map: meta, baseUrl: imageBaseURL });
      await queryClient.invalidateQueries(['gallery']);
      setLoading(false);
    };

    const state = useDropzone({
      onDrop: onInput,
      accept: {
        'image/png': ['.png'],
        'image/jpeg': ['.jpeg', '.jpg']
      }
    });

    const deleteModalProps = useModal<{ image: ImageMetadata }>();

    return (
      <>
        <DeleteGalleryImageModal {...deleteModalProps} />
        <div className="flex flex-col gap-4 py-4">
          <FileInputContainer state={state} loading={loading} placeholder={`Click or drop images to upload`} />
          <UploadManager />
          <hr />
          <TextInput
            prefix={<SiSearch className="text-xl mr-2" />}
            id="gallery-query"
            value={query}
            onChange={setQuery}
            placeholder="Search images"
          />
          <div className="grid grid-cols-3 gap-3 overflow-y-scroll h-96">
            {results.map(image => (
              <GalleryModalCard
                key={image.id}
                image={image}
                onSelectCallback={onSelect}
                onDeleteCallback={() => deleteModalProps.open({ image })}
              />
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal className="md:max-w-5xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Gallery</h1>
        </Modal.Controls>
        <Suspense fallback={<Loading />}>
          <Gallery />
        </Suspense>
      </Card>
    </Modal>
  );
};
